import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ShareIcon from '@mui/icons-material/Share';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import ActivityFeed from '../components/common/ActivityFeed';

const Dashboard = () => {
  const { user, loadUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [donationStats, setDonationStats] = useState({
    totalDonated: 0,
    donationCount: 0,
  });

  useEffect(() => {
    if (user) {
      loadUserEvents();
      loadDonationStats();
    }
  }, [user]);

  const loadUserEvents = async () => {
    try {
      setLoading(true);
      const res = await api.get('/events');
      const userEvents = res.data.data.filter(
        (event) =>
          event.organizer?._id === user?.id ||
          event.organizer?._id?.toString() === user?.id ||
          event.attendees?.some(
            (attendee) =>
              attendee._id === user?.id || attendee._id?.toString() === user?.id
          )
      );
      setEvents(userEvents.slice(0, 5));
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDonationStats = async () => {
    try {
      // Load user's donation history
      const res = await api.get(`/donations/user/${user?.id}`);
      if (res.data.data) {
        const total = res.data.data.reduce((sum, donation) => sum + donation.amount, 0);
        setDonationStats({
          totalDonated: total,
          donationCount: res.data.data.length,
        });
      }
    } catch (error) {
      console.error('Error loading donation stats:', error);
    }
  };

  const handleCopyReferral = () => {
    const referralCode = user?.referralCode || 'GBCN-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const formatLocation = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    const { city, state, country } = loc;
    return [city, state, country].filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          background:
            'radial-gradient(circle at top left, #ffedd5 0%, #f9fafb 40%, #dbeafe 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const firstName = user?.name?.split(' ')?.[0] || 'Member';

  return (
    <Box
      sx={{
        minHeight: '80vh',
        py: 6,
        background:
          'radial-gradient(circle at top left, #fffbeb 0%, #f9fafb 45%, #e0f2fe 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            mb: 4,
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#111827',
                mb: 0.5,
              }}
            >
              Welcome back, {firstName}.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#4b5563' }}
            >
              This is your personal GBCN dashboard – track your giving, events, and community activity.
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderRadius: 999,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <AccountBalanceIcon sx={{ color: '#f97316' }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.08em' }}
              >
                Total given
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: '#111827' }}
              >
                ₹{donationStats.totalDonated.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Profile Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                mb: 3,
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Avatar
                  src={user?.profileImage}
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: '#f97316',
                    fontSize: '3rem',
                  }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </Avatar>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#111827' }}>
                  {user?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {user?.email}
                </Typography>
                {user?.location && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatLocation(user.location)}
                  </Typography>
                )}
                <Button
                  component={Link}
                  to="/profile"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{
                    borderColor: '#f97316',
                    color: '#f97316',
                    '&:hover': { borderColor: '#ea580c', backgroundColor: 'rgba(249, 115, 22, 0.1)' },
                  }}
                >
                  Edit Profile
                </Button>
              </CardContent>
            </Card>

            {/* Donation Stats */}
            <Card
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
                mb: 3,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <VolunteerActivismIcon sx={{ fontSize: 32, color: '#f97316', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    My Donations
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316', mb: 1 }}>
                  ₹{donationStats.totalDonated.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {donationStats.donationCount} donation{donationStats.donationCount !== 1 ? 's' : ''}
                </Typography>
                <Button
                  component={Link}
                  to="/transparency"
                  variant="text"
                  size="small"
                  sx={{ mt: 2, color: '#f97316' }}
                >
                  View Details →
                </Button>
              </CardContent>
            </Card>

            {/* Referral Card */}
            <Card
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ShareIcon sx={{ fontSize: 32, color: '#f97316', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    Refer Members
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Share your referral link to grow the community
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCopyReferral}
                  startIcon={<ShareIcon />}
                  sx={{
                    borderColor: '#f97316',
                    color: '#f97316',
                    '&:hover': { borderColor: '#ea580c', backgroundColor: 'rgba(249, 115, 22, 0.1)' },
                  }}
                >
                  Copy Referral Link
                </Button>
                <Button
                  component={Link}
                  to="/members"
                  variant="text"
                  size="small"
                  fullWidth
                  sx={{ mt: 1, color: '#f97316' }}
                >
                  View Members →
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Events Section */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 18px 45px rgba(15,23,42,0.06)',
                mb: 3,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827' }}>
                    My Events
                  </Typography>
                  <Button
                    component={Link}
                    to="/events"
                    variant="contained"
                    sx={{
                      backgroundColor: '#f97316',
                      '&:hover': { backgroundColor: '#ea580c' },
                    }}
                  >
                    View All
                  </Button>
                </Box>
                {events.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <EventIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      You haven't registered for any events yet
                    </Typography>
                    <Button
                      component={Link}
                      to="/events"
                      variant="contained"
                      sx={{
                        backgroundColor: '#f97316',
                        '&:hover': { backgroundColor: '#ea580c' },
                      }}
                    >
                      Browse Events
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {events.map((event) => (
                      <Box
                        key={event._id}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          '&:hover': { borderColor: '#f97316', backgroundColor: '#f9fafb' },
                        }}
                      >
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#111827' }}>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(event.date).toLocaleDateString()} •{' '}
                          {formatLocation(event.location)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
            <ActivityFeed userId={user?._id} limit={10} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
